import Alert from 'react-bootstrap/Alert';
import { useEffect, useMemo } from 'react';
import { FormikErrors } from 'formik';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { ImageAttribute } from '../common/image-attribute/image-attribute';
import { AttributeTypes } from '@iddataweb/idw-common-ui/enums';
import { useSingleAttributeImage } from '@hooks/use-single-attribute-image';
import { useSelector } from 'react-redux';
import { getLanguageContent } from '@redux/selectors/language-selectors';
import styles from "./single-attribute-image.less";

type SingleAttributeImageProps = {
    onSetSubmitValue: (payload: any) => void;
    onSetPickerValue: (payload: any) => void;
    attribute: AttributeFormState;
    fieldName: string;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    attributeName: string;
};

export const SingleAttributeImage = (props: SingleAttributeImageProps) => {
    const {
        attribute,
        fieldName,
        setFieldValue,
        setFieldTouched,
        onSetSubmitValue,
        onSetPickerValue,
        attributeName
    } = props;

    const content = useSelector(getLanguageContent);
    const images = useSingleAttributeImage(attribute, attributeName, onSetSubmitValue, onSetPickerValue);
    const [primaryImage] = images;

    const setFieldValues = async (image: string) => {
        if (image) {
            await setFieldValue(`${fieldName}.${attributeName}`, image);
            setFieldTouched(`${fieldName}.${attributeName}`, true, true);
        } else {
            await setFieldValue(`${fieldName}.${attributeName}`, '');
            setFieldTouched(`${fieldName}.${attributeName}`, true, true);
        }
    };

    useEffect(() => {
        setFieldValues(primaryImage);
    }, [primaryImage]);

    const missingImageText = useMemo(() => {
        switch (attribute.type) {
            case AttributeTypes.PASSPORT_IMAGE:
                return content.form.passportImage.missingFrontImage;
            case AttributeTypes.CAMERA_SNAPSHOT:
                return content.form.cameraSnapshot.missingImage;
            case AttributeTypes.AUDIT_TRAIL_IMAGE:
                return content.form.auditTrailImage.missingImage;
            default:
                return "";
        }
    }, [attribute.type, content]);

    const imageLabels = useMemo(() => {
        switch (attribute.type) {
            case AttributeTypes.PASSPORT_IMAGE:
                return ["Preview Image of Passport"];
            case AttributeTypes.CAMERA_SNAPSHOT:
                return ["Preview Selfie Image"];
            case AttributeTypes.AUDIT_TRAIL_IMAGE:
                return ["Preview Audit Trail Image"];
            default:
                return [""];
        }
    }, [attribute.type]);

    return (
        <div className={styles.singleAttributeImage}>
            {!primaryImage && (
                <Alert variant={"danger"}>
                    {missingImageText}
                </Alert>
            )}
            {primaryImage && (
                <ImageAttribute
                    id={`image-attribute-${attribute.id}`}
                    images={images}
                    showGalleryInitially={false}
                    centerPreviewMessage={true}
                    imageLabels={imageLabels}
                />
            )}
        </div>
    );
};